import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { appRepo } from "../../repo/app-repo.ts";
import { AppContext } from "../../context/app-context.tsx";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { DonationData, PaymentStatus } from "../../entry/server/app.ts";
import { Pagination } from "../../lib/api.ts";
import PaginationView from "../../componrnts/pagination.tsx";

const PAGE_LIMITS = [10, 20] as const;

const STATUS_OPTIONS: { label: string; value: "" | PaymentStatus }[] = [
  { label: "همه", value: "" },
  { label: "در انتظار", value: "pending" },
  { label: "پرداخت‌شده", value: "paid" },
  { label: "ناموفق", value: "failed" },
];

function formatToman(amount: number) {
  const value = Number.isFinite(amount) ? Math.round(amount) : 0;
  return `${value.toLocaleString("fa-IR")} تومان`;
}

function getStatusMeta(status: string) {
  const normalized = (status || "").toLowerCase() as PaymentStatus;

  if (normalized === "pending") {
    return { label: "در انتظار", className: "bg-sky-500/20 text-sky-300" };
  }
  if (normalized === "paid") {
    return { label: "پرداخت‌شده", className: "bg-emerald-500/20 text-emerald-300" };
  }
  if (normalized === "failed") {
    return { label: "ناموفق", className: "bg-rose-500/20 text-rose-300" };
  }

  return { label: status || "—", className: "bg-slate-700/60 text-slate-300" };
}

export default function DonationsList() {
  const [donations, setDonations] = useState<DonationData[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<(typeof PAGE_LIMITS)[number]>(10);
  const [statusFilter, setStatusFilter] = useState<"" | PaymentStatus>("");
  const { setMessage } = useContext(AppContext);

  useEffect(() => {
    const fetchDonations = async () => {
      setLoading(true);
      try {
        const response = await appRepo.getDonations(page, limit, statusFilter || undefined);
        if (!response.status || !response.data) {
          setMessage({ type: "error", message: response.message || "دریافت لیست حمایت‌ها ناموفق بود." });
          setDonations([]);
          return;
        }
        setDonations(response.data);
        setPagination(response.pagination ?? null);
      } catch {
        setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
        setDonations([]);
      } finally {
        setLoading(false);
      }
    };

    void fetchDonations();
  }, [page, limit, statusFilter, setMessage]);

  return (
    <section className="space-y-4">
      <AdminPageTitle title="حمایت‌ها" />

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <label className="space-y-2 sm:w-48">
          <span className="text-sm text-slate-300">وضعیت</span>
          <select
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
            value={statusFilter}
            onChange={(event) => {
              setPage(1);
              setStatusFilter(event.target.value as "" | PaymentStatus);
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-2 sm:w-36">
          <span className="text-sm text-slate-300">تعداد در صفحه</span>
          <select
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
            value={limit}
            onChange={(event) => {
              setPage(1);
              setLimit(Number(event.target.value) as (typeof PAGE_LIMITS)[number]);
            }}
          >
            {PAGE_LIMITS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت لیست حمایت‌ها...
        </div>
      ) : !donations.length ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">حمایتی یافت نشد.</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60">
            <table className="min-w-full divide-y divide-slate-800 text-sm">
              <thead className="bg-slate-900/80">
                <tr>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">شناسه</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">کاربر</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">نام</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">ایمیل</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">مبلغ</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">درگاه</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">وضعیت</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">یادداشت</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">تاریخ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {donations.map((donation) => {
                  const statusMeta = getStatusMeta(donation.status);
                  const isGuest = donation.user_id == null;
                  return (
                    <tr key={donation.id} className="transition hover:bg-slate-900">
                      <td className="whitespace-nowrap px-4 py-3 text-slate-300">{donation.id}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-100">
                        {isGuest ? (
                          <span className="text-slate-400">مهمان</span>
                        ) : (
                          <Link
                            to={`/admin/users/${donation.user_id}`}
                            className="text-indigo-300 transition hover:text-indigo-200"
                          >
                            {donation.username || `#${donation.user_id}`}
                          </Link>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-300">{donation.name || "—"}</td>
                      <td className="max-w-[12rem] truncate px-4 py-3 text-slate-300" title={donation.email}>
                        {donation.email || "—"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-100">{formatToman(donation.amount)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-300">
                        {donation.gateway_name || (donation.gateway_id ? `#${donation.gateway_id}` : "—")}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs ${statusMeta.className}`}>
                          {statusMeta.label}
                        </span>
                      </td>
                      <td className="max-w-[14rem] truncate px-4 py-3 text-slate-400" title={donation.note}>
                        {donation.note || "—"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-400">{donation.created_at || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pagination ? <PaginationView pagination={pagination} onPageChange={setPage} /> : null}
        </>
      )}
    </section>
  );
}
